import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { api, query } from '../lib/api.ts';
import { dataBr, formatarCpf, idade } from '../lib/formato.ts';
import './buscaPaciente.css';

export interface PacienteResumo {
  id: string;
  nome: string;
  cpf: string | null;
  nascimento: string;
  matricula: string | null;
}

/** Seleção de paciente por digitação.
 *  O sistema atual usa um <select> com a lista inteira de pacientes; com o
 *  cadastro real isso vira uma lista de milhares de linhas que ninguém rola.
 *  Aqui a pessoa digita nome, CPF, matrícula ou data de nascimento — os mesmos
 *  critérios da tela de Pacientes — e escolhe pelo teclado ou pelo clique. */
export function BuscaPaciente({
  valor,
  aoEscolher,
  erro,
  desabilitado,
  aoCadastrar,
}: {
  valor: PacienteResumo | null;
  aoEscolher: (p: PacienteResumo | null) => void;
  erro?: string;
  desabilitado?: boolean;
  /** Onde há tela para cadastrar sem sair, "não encontrei" vira um caminho em
   *  vez de um beco. Recebe o que foi digitado, para não redigitar o nome. */
  aoCadastrar?: (digitado: string) => void;
}) {
  const id = useId();
  const [texto, setTexto] = useState('');
  const [termo, setTermo] = useState('');
  const [aberto, setAberto] = useState(false);
  const [destacado, setDestacado] = useState(0);
  const caixa = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setTermo(texto.trim()), 220);
    return () => clearTimeout(t);
  }, [texto]);

  // Clique fora fecha a lista sem escolher nada.
  useEffect(() => {
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);

  const busca = useQuery({
    queryKey: ['pacientes', 'busca', termo],
    queryFn: ({ signal }) =>
      api.get<{ itens: PacienteResumo[] }>(
        `/pacientes${query({ busca: termo, porPagina: 10 })}`,
        signal,
      ),
    enabled: termo.length >= 2,
    staleTime: 30_000,
  });

  const itens = busca.data?.itens ?? [];

  function escolher(p: PacienteResumo) {
    aoEscolher(p);
    setTexto('');
    setAberto(false);
  }

  function teclado(e: React.KeyboardEvent) {
    if (!aberto || itens.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setDestacado((d) => (d + 1) % itens.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDestacado((d) => (d - 1 + itens.length) % itens.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const p = itens[destacado];
      if (p) escolher(p);
    } else if (e.key === 'Escape') {
      setAberto(false);
    }
  }

  if (valor) {
    return (
      <div className="campo campo--total">
        <span className="campo__rotulo rotulo">
          Paciente<span className="campo__obrigatorio">*</span>
        </span>
        <div className="paciente-escolhido">
          <div className="paciente-escolhido__dados">
            <strong>{valor.nome}</strong>
            <span className="paciente-escolhido__apoio mono">
              {dataBr(valor.nascimento)} · {idade(valor.nascimento)} anos
              {valor.cpf ? ` · ${formatarCpf(valor.cpf)}` : ''}
              {valor.matricula ? ` · mat. ${valor.matricula}` : ''}
            </span>
          </div>
          {!desabilitado && (
            <button
              type="button"
              className="paciente-escolhido__trocar"
              onClick={() => aoEscolher(null)}
            >
              Trocar
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="campo campo--total" data-erro={erro ? '' : undefined} ref={caixa}>
      <label className="campo__rotulo rotulo" htmlFor={id}>
        Paciente<span className="campo__obrigatorio">*</span>
      </label>

      <div className="autocompletar">
        <input
          id={id}
          className="entrada"
          type="search"
          autoComplete="off"
          role="combobox"
          aria-expanded={aberto}
          aria-controls={`${id}-lista`}
          aria-autocomplete="list"
          placeholder="Nome, CPF, matrícula ou data de nascimento"
          value={texto}
          disabled={desabilitado}
          onChange={(e) => {
            setTexto(e.target.value);
            setAberto(true);
            setDestacado(0);
          }}
          onFocus={() => setAberto(true)}
          onKeyDown={teclado}
        />

        {aberto && termo.length >= 2 && (
          <ul className="autocompletar__lista" id={`${id}-lista`} role="listbox">
            {busca.isFetching && itens.length === 0 && (
              <li className="autocompletar__aviso">Procurando…</li>
            )}
            {/* Sem saída aqui, quem chega com paciente novo precisa sair da
                tela, cadastrar em Pacientes, voltar e buscar de novo -- e o que
                já estava preenchido no exame se perde. Onde existe para onde
                ir, o próprio "não encontrei" vira o caminho. */}
            {!busca.isFetching && itens.length === 0 && (
              <li>
                {aoCadastrar ? (
                  <button
                    type="button"
                    className="autocompletar__item autocompletar__cadastrar"
                    onClick={() => {
                      aoCadastrar(texto.trim());
                      setAberto(false);
                    }}
                  >
                    <span className="autocompletar__nome">Cadastrar “{texto.trim()}”</span>
                    <span className="autocompletar__apoio">
                      Nenhum paciente encontrado com esse dado.
                    </span>
                  </button>
                ) : (
                  <span className="autocompletar__aviso">
                    Nenhum paciente encontrado. Cadastre em Pacientes primeiro.
                  </span>
                )}
              </li>
            )}
            {itens.map((p, i) => (
              <li key={p.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === destacado}
                  className="autocompletar__item"
                  data-destacado={i === destacado || undefined}
                  onMouseEnter={() => setDestacado(i)}
                  onClick={() => escolher(p)}
                >
                  <span className="autocompletar__nome">{p.nome}</span>
                  <span className="autocompletar__apoio mono">
                    {dataBr(p.nascimento)}
                    {p.cpf ? ` · ${formatarCpf(p.cpf)}` : ''}
                    {p.matricula ? ` · mat. ${p.matricula}` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {erro ? (
        <p className="campo__erro" role="alert">
          {erro}
        </p>
      ) : (
        <p className="campo__dica">Digite pelo menos duas letras.</p>
      )}
    </div>
  );
}
